import { FunctionComponent, useRef, useEffect } from "react";
import Map, {
  Source,
  Layer,
  LineLayer,
  CircleLayer,
  NavigationControl,
  MapRef,
} from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Feature } from "geojson";
import { JesprActivity } from "./Activity";
import { JesprPublicActivity } from "../../public/PublicActivity";

const MAPBOX_STYLE = "robertoehler/ckses8ubo25xx18nvizkw2e0l";

interface Props {
  activity: JesprActivity | JesprPublicActivity;
}

const ActivityMap: FunctionComponent<Props> = ({ activity }) => {
  const mapRef = useRef<MapRef>(null);

  useEffect(() => {
    const hasData = activity.dataPoints && activity.dataPoints.length > 0;
    if (mapRef.current && hasData) {
      const latestPoint = activity.dataPoints[activity.dataPoints.length - 1];
      const latestCoord = [
        latestPoint.coordinates[1],
        latestPoint.coordinates[0],
      ] as [number, number];

      mapRef.current.flyTo({
        center: latestCoord,
        duration: 1200,
        zoom: mapRef.current.getZoom(),
      });
    }
  }, [activity.dataPoints]);

  if (!activity.dataPoints || activity.dataPoints.length === 0) {
    console.warn("Activity contains no data points. Cannot render map.");
    return <div>No activity data to display.</div>;
  }

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLon = Infinity;
  let maxLon = -Infinity;

  for (const dp of activity.dataPoints) {
    const [lat, lon] = dp.coordinates;
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLon = Math.min(minLon, lon);
    maxLon = Math.max(maxLon, lon);
  }

  const bounds: [[number, number], [number, number]] = [
    [minLon, minLat],
    [maxLon, maxLat],
  ];

  const lineFeature: Feature = {
    type: "Feature",
    properties: {},
    geometry: {
      type: "LineString",
      coordinates: activity.dataPoints.map((dp) =>
        dp.coordinates.slice().reverse(),
      ),
    },
  };

  const lineLayer: LineLayer = {
    id: "activity",
    type: "line",
    source: "",
    layout: {
      "line-join": "round",
      "line-cap": "round",
    },
    paint: {
      "line-color": "#00f096",
      "line-width": 5,
    },
  };

  const pointFeature: Feature = {
    type: "Feature",
    properties: {},
    geometry: {
      type: "Point",
      coordinates: activity.dataPoints[
        activity.dataPoints.length - 1
      ].coordinates
        .slice()
        .reverse(),
    },
  };

  const circleLayer: CircleLayer = {
    id: "current-position",
    type: "circle",
    source: "",
    paint: {
      "circle-radius": 7,
      "circle-color": "#00f096",
    },
  };

  return (
    <Map
      ref={mapRef}
      mapboxAccessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}
      initialViewState={{
        bounds,
        fitBoundsOptions: {
          padding: 50,
        },
      }}
      scrollZoom={false}
      mapStyle={`mapbox://styles/${MAPBOX_STYLE}`}
    >
      <Source type="geojson" data={lineFeature}>
        <Layer {...lineLayer} />
      </Source>
      {activity.active && activity.dataPoints.length > 0 && (
        <Source type="geojson" data={pointFeature}>
          <Layer {...circleLayer} />
        </Source>
      )}
      <NavigationControl />
    </Map>
  );
};

export default ActivityMap;
